import * as React from 'react'
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers'
import { BaseInputFieldProps } from '@globaleris/ui.library/src/lib/components/dataTable'

import MomentUtils from '@date-io/moment'
import moment from 'moment'
import 'moment/locale/es'
import i18next from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date'

const DEFAULT_LOCALE = 'es'
export const DEFAULT_DATE_FORMAT = 'DD/MM/YYYY'
export const DATE_FORMATS = { es: 'DD/MM/YYYY', en: 'MM/DD/YYYY' }

export interface InputDateProps extends BaseInputFieldProps {
  value?: string
  placeholder?: string
  inputProps?: any
  disablePast?: boolean
  disableFuture?: boolean
  minDate?: string
  maxDate?: string
}

export const InputDate: React.FC<InputDateProps> = ({
  name,
  label,
  placeholder,
  onChange,
  onFocus,
  onBlur,
  helpText = '',
  value,
  disabled = false,
  required = false,
  inputProps,
  inputType,
  disablePast,
  disableFuture,
  minDate,
  maxDate
}) => {
  const [currentLocale, setCurrentLocale] = React.useState(DEFAULT_LOCALE)
  const [dateFormat, setDateFormat] = React.useState(DEFAULT_DATE_FORMAT)

  const [selectedDate, setSelectedDate] = React.useState<MaterialUiPickersDate>(
    value ? moment(value) : null
  )

  const [dateIsInvalid, setdateIsInvalid] = React.useState(
    value === undefined ? false : required && !value
  )

  const [minimunValidDate, setMinimunValidDate] = React.useState<moment.Moment | undefined>(
    undefined
  )
  const [maximunValidDate, setMaximunValidDate] = React.useState<moment.Moment | undefined>(
    undefined
  )

  React.useEffect(() => {
    let minimunValidDate
    if (minDate) {
      minimunValidDate = moment(minDate)
    }
    if (disablePast) {
      minimunValidDate = moment()
    }
    setMinimunValidDate(minimunValidDate)
  }, [minDate, disablePast])

  React.useEffect(() => {
    let maximunValidDate
    if (maxDate) {
      maximunValidDate = moment(maxDate)
    }
    if (disableFuture) {
      maximunValidDate = moment()
    }
    setMaximunValidDate(maximunValidDate)
  }, [maxDate, disableFuture])

  React.useEffect(() => {
    i18next
      .use(LanguageDetector)
      .init()
      .then(t => {
        moment.locale(i18next.language)
        setCurrentLocale(i18next.language)
        setDateFormat(DATE_FORMATS[i18next.language] || DEFAULT_DATE_FORMAT)
      })
  }, [])

  const handleChange = (newDate: MaterialUiPickersDate) => {
    let isInvalid = (required && !newDate) || (newDate && !newDate.isValid())
    if (!isInvalid && minimunValidDate && newDate && newDate.isBefore(minimunValidDate, 'day')) {
      isInvalid = true
    }
    if (!isInvalid && maximunValidDate && newDate && newDate.isAfter(maximunValidDate, 'day')) {
      isInvalid = true
    }
    setdateIsInvalid(isInvalid || false)
    setSelectedDate(newDate)

    onChange(newDate && newDate.toISOString(), isInvalid || false)
  }

  const handleBlur = React.useCallback(
    event => {
      const value = event.target.value
      onBlur && onBlur(value)
    },
    [onBlur]
  )

  return (
    <MuiPickersUtilsProvider libInstance={moment} utils={MomentUtils} locale={currentLocale}>
      <KeyboardDatePicker
        disablePast={disablePast}
        disableFuture={disableFuture}
        minDate={minDate}
        maxDate={maxDate}
        inputVariant={inputType}
        color="primary"
        fullWidth
        id={name}
        name={name}
        label={label}
        autoOk
        value={selectedDate || null}
        variant="inline"
        placeholder={placeholder ? moment(placeholder).format(dateFormat) : undefined}
        format={dateFormat}
        onChange={handleChange}
        onFocus={onFocus}
        onBlur={handleBlur}
        helperText={helpText}
        disabled={disabled}
        required={required}
        error={dateIsInvalid}
        InputLabelProps={{
          shrink: true
        }}
        InputProps={inputProps}
      />
    </MuiPickersUtilsProvider>
  )
}
